import { TypographyStyle } from '@mui/material';
import { pxToRem, responsiveFontSizes } from '../utils/getFontValue';

//
//

declare module '@mui/material/styles' {
  interface TypographyVariants {
    title1: TypographyStyle;
    title2: TypographyStyle;
    body3: TypographyStyle;
    tableHeader: TypographyStyle;
    overlineRegular: TypographyStyle;
    system: TypographyStyle;
    ticker1: TypographyStyle;
    ticker1Label: TypographyStyle;
    ticker2: TypographyStyle;
    ticker2Label: TypographyStyle;
    badge: TypographyStyle;
    code: TypographyStyle;
  }
}

declare module '@mui/material/Typography/Typography' {
  interface TypographyPropsVariantOverrides {
    title1: true;
    title2: true;
    body3: true;
    tableHeader?: true;
    overlineRegular?: true;
    system?: true;
    ticker1?: true;
    ticker1Label?: true;
    ticker2?: true;
    ticker2Label?: true;
    badge?: true;
    code?: true;
  }
}

// Cloudinary + system fonts

const FONT_PRIMARY = 'ObjektivMk1, Outfit, Trebuchet MS, sans-serif';
export const FONT_SECONDARY = 'Mirador, Newsreader, Georgia, serif';
export const CODE_FONT = 'Courier New, sans-serif';

//

const typography = {
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  title1: {
    lineHeight: 1.1,
    fontSize: pxToRem(28),
    fontFamily: FONT_SECONDARY,
    display: 'block',
    fontVariantNumeric: 'lining-nums',
    ...responsiveFontSizes({ sm: 28, md: 32, lg: 36 }),
  },
  title2: {
    lineHeight: 1.3,
    fontSize: pxToRem(24),
    fontFamily: FONT_SECONDARY,
    display: 'block',
    fontVariantNumeric: 'lining-nums',
  },
  h1: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    lineHeight: 1.25,
    fontSize: pxToRem(40),
    letterSpacing: 2,
    ...responsiveFontSizes({ sm: 40, md: 50, lg: 64 }),
  },
  h2: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    lineHeight: 60 / 44,
    fontSize: pxToRem(32),
    ...responsiveFontSizes({ sm: 32, md: 38, lg: 44 }),
  },
  h3: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(24),
    ...responsiveFontSizes({ sm: 24, md: 28, lg: 32 }),
  },
  h4: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(20),
    ...responsiveFontSizes({ sm: 20, md: 24, lg: 24 }),
  },
  h5: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(18),
    ...responsiveFontSizes({ sm: 18, md: 20, lg: 20 }),
  },
  h6: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(17),
    ...responsiveFontSizes({ sm: 17, md: 18, lg: 18 }),
  },
  subtitle1: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.4,
    fontSize: pxToRem(18),
  },
  subtitle2: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  body1: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  body2: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  body3: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.75,
    fontSize: pxToRem(12),
    display: 'block',
  },
  caption: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
  },
  tableHeader: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.75,
    fontWeight: 800,
    fontSize: pxToRem(16),
    display: 'block',
  },
  overline: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
    letterSpacing: 0,
  },
  overlineRegular: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
    display: 'block',
    letterSpacing: 0,
  },
  system: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.6,
    fontWeight: 600,
    fontSize: pxToRem(10),
    display: 'block',
    textTransform: 'uppercase',
  },
  code: {
    fontFamily: CODE_FONT,
    lineHeight: 1.3,
    fontSize: pxToRem(12),
    display: 'block',
  },
  ticker1: {
    fontFamily: FONT_SECONDARY,
    lineHeight: 1.5,
    fontSize: pxToRem(24),
    display: 'block',
    fontVariantNumeric: 'lining-nums',
  },
  ticker1Label: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(10),
    display: 'block',
    textTransform: 'uppercase',
  },
  ticker2: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(24),
    display: 'block',
    fontVariantNumeric: 'lining-nums',
  },
  ticker2Label: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(10),
    display: 'block',
    textTransform: 'uppercase',
  },
  badge: {
    fontFamily: FONT_PRIMARY,
    fontWeight: 800,
    fontSize: pxToRem(11),
  },
  button: {
    fontFamily: FONT_PRIMARY,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
    textTransform: 'inherit',
  },
} as const;

export default typography;
